import { useState } from "react";
import AmountInput from "../components/AmountInput";
import { EDITABLE_PAYMENT_TYPES } from "../constants";
import { smallInput, t, tabular } from "../styles";
import { money, n, ronMoney } from "../utils";

const COLUMNS = "40px 150px minmax(140px,1fr) 150px 160px 32px";

const vatLabel = (v) => `${Number(v.percentage)}%${v.name ? ` ${v.name}` : ""}`;

const Tile = ({ label, value, ron, meta, danger }) => (
  <div style={{ background: danger ? t.dangerBg : "#fff", padding: "13px 16px" }}>
    <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: "uppercase", color: t.textMuted }}>{label}</div>
    <div style={{ fontSize: 19, fontWeight: 650, marginTop: 4, color: danger ? t.danger : t.text, ...tabular }}>{value}</div>
    {ron && <span style={{ display: "block", fontSize: 11.5, fontWeight: 500, color: "#6f6f7c", marginTop: 2, ...tabular }}>{ron}</span>}
    <div style={{ fontSize: 12, color: danger ? t.danger : "#6f6f7c", marginTop: 2 }}>{meta}</div>
  </div>
);

const LockIcon = ({ title }) => (
  <span title={title} style={{ width: 30, height: 34, display: "flex", alignItems: "center", justifyContent: "center", color: "#a0a0ac" }}>
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  </span>
);

const VatSelect = ({ value, onChange, vatRates }) => (
  <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} style={smallInput("#dcdce3", { padding: "0 8px" })}>
    {!vatRates.length && <option value="">No VAT rates</option>}
    {vatRates.map((v) => (
      <option key={v.id} value={String(v.id)}>
        {vatLabel(v)}
      </option>
    ))}
  </select>
);

/**
 * Step 4: payment schedule. Payments sort by due date; undated ones sit at
 * the end and can be dragged. Two rows are generated, not typed: the extras
 * row (sum of unit extras) and the balance row (always last, takes whatever
 * the other payments leave of the contract total).
 */
const PaymentsStep = ({
  pays,
  extrasDate,
  extrasVat,
  balanceVat,
  vatRates,
  totals,
  showRon,
  rate,
  onUpdatePay,
  onRemovePay,
  onAddPay,
  onExtrasDate,
  onExtrasVat,
  onBalanceVat,
  onAllVat,
  onMove,
}) => {
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const ron = (x) => (showRon && rate ? ronMoney(x, rate) : "");

  const visible = pays.filter((p) => p.id !== "extras" || totals.extras > 0);
  const vatSet = Array.from(
    new Set(
      pays
        .filter((p) => p.id !== "extras")
        .map((p) => String(p.vat))
        .concat(totals.extras > 0 ? [String(extrasVat)] : [], [String(balanceVat)])
    )
  );
  const vatCommon = vatSet.length === 1 ? vatSet[0] : "";
  const rowCount = visible.length + 1;

  const dragProps = (id) => ({
    draggable: true,
    onDragStart: (e) => {
      e.dataTransfer.effectAllowed = "move";
      try {
        e.dataTransfer.setData("text/plain", String(id));
      } catch (err) {
        // Some browsers refuse setData on certain elements; the drag still works via state.
      }
      setDragId(id);
    },
    onDragOver: (e) => {
      e.preventDefault();
      if (overId !== id) setOverId(id);
    },
    onDrop: (e) => {
      e.preventDefault();
      if (dragId !== null && dragId !== id) onMove(dragId, id);
      setDragId(null);
      setOverId(null);
    },
    onDragEnd: () => {
      setDragId(null);
      setOverId(null);
    },
  });

  const rowStyle = (id, bg) => ({
    display: "grid",
    gridTemplateColumns: COLUMNS,
    gap: 10,
    minWidth: 760,
    alignItems: "center",
    padding: "9px 14px",
    borderBottom: "1px solid #f4f4f7",
    borderTop: `2px solid ${overId === id && dragId !== null && dragId !== id ? t.accent : "transparent"}`,
    background: bg,
    opacity: dragId === id ? 0.45 : 1,
  });

  const handle = (i, grab = true) => (
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      <span title={grab ? "Drag to reorder" : undefined} style={{ cursor: grab ? "grab" : "default", color: grab ? "#b6b6c2" : "transparent", fontSize: 13, letterSpacing: -2, width: 12, userSelect: "none" }}>
        ⋮⋮
      </span>
      <span style={{ fontSize: 12.5, color: t.textMuted, width: 14, textAlign: "center", ...tabular }}>{i + 1}</span>
    </div>
  );

  const lockedType = (title, note) => (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 13.5, fontWeight: 650 }}>{title}</div>
      <div style={{ fontSize: 11, color: "#6f6f7c", marginTop: 1 }}>{note}</div>
    </div>
  );

  const lockedAmount = (value, danger) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, height: 34, padding: "0 10px", fontSize: 13.5, fontWeight: 650, color: danger ? t.danger : t.text, minWidth: 0, ...tabular }}>
      {ron(value) && <span style={{ fontSize: 11.5, fontWeight: 500, color: t.textMuted, whiteSpace: "nowrap" }}>{ron(value)}</span>}
      <span style={{ marginLeft: "auto", whiteSpace: "nowrap" }}>{money(value)}</span>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 1, background: t.border, border: `1px solid ${t.border}`, borderRadius: 12, overflow: "hidden" }}>
        <Tile label="Contract total" value={money(totals.total)} ron={ron(totals.total)} meta={`${money(totals.settle)} settlement + ${money(totals.extras)} extras`} />
        <Tile label="Scheduled" value={money(totals.scheduled)} ron={ron(totals.scheduled)} meta={`${rowCount - 1} dated payments`} />
        <Tile
          label="Balance (remainder)"
          value={money(totals.balance)}
          ron={ron(totals.balance)}
          danger={totals.balance < 0}
          meta={totals.balance < 0 ? "Over the total — reduce a payment" : totals.balance === 0 ? "Nothing left for the balance" : "Due at handover, no date"}
        />
      </div>

      <div style={{ border: `1px solid ${t.border}`, borderRadius: 12, overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "11px 14px", borderBottom: "1px solid #eeeef2" }}>
          <span style={{ fontSize: 13, fontWeight: 650 }}>VAT for all payments</span>
          <select
            value={vatCommon}
            onChange={(e) => e.target.value && onAllVat(e.target.value)}
            style={smallInput("#dcdce3", { width: 170, padding: "0 8px" })}
          >
            {!vatCommon && <option value="">Mixed</option>}
            {vatRates.map((v) => (
              <option key={v.id} value={String(v.id)}>
                {vatLabel(v)}
              </option>
            ))}
          </select>
          <span style={{ fontSize: 12, color: vatCommon ? "#6f6f7c" : "#8a6b12" }}>
            {vatCommon ? "Applied to every payment · you can still change a single row" : "Mixed rates — pick one to apply it to every payment"}
          </span>
        </div>

        <div style={{ overflowX: "auto" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: COLUMNS,
              gap: 10,
              minWidth: 760,
              padding: "9px 14px",
              background: t.surfaceSubtle,
              borderBottom: "1px solid #eeeef2",
              fontSize: 10.5,
              fontWeight: 650,
              letterSpacing: 0.6,
              textTransform: "uppercase",
              color: t.textMuted,
            }}
          >
            <span>#</span>
            <span>Type</span>
            <span style={{ textAlign: "right" }}>Amount (EUR)</span>
            <span>VAT</span>
            <span>Due date</span>
            <span />
          </div>

          {visible.map((p, i) =>
            p.id === "extras" ? (
              <div key="extras" {...dragProps("extras")} style={rowStyle("extras", "#fcfcfd")}>
                {handle(i)}
                {lockedType("Extras", "Sum of unit extras")}
                {lockedAmount(totals.extras)}
                <VatSelect value={extrasVat} onChange={onExtrasVat} vatRates={vatRates} />
                <input type="date" value={extrasDate} onChange={(e) => onExtrasDate(e.target.value)} style={smallInput(!extrasDate ? "#e3a09d" : "#dcdce3", { padding: "0 8px" })} />
                <LockIcon title="Created from unit extras" />
              </div>
            ) : (
              <div key={p.id} {...dragProps(p.id)} style={rowStyle(p.id, "#fff")}>
                {handle(i)}
                <select value={p.type} onChange={(e) => onUpdatePay(p.id, { type: e.target.value })} style={smallInput("#dcdce3", { padding: "0 8px" })}>
                  {EDITABLE_PAYMENT_TYPES.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <div style={{ position: "relative", minWidth: 0 }}>
                  <AmountInput
                    value={p.amount}
                    onChange={(v) => onUpdatePay(p.id, { amount: v })}
                    placeholder="0.00"
                    style={smallInput(n(p.amount) <= 0 ? "#e3a09d" : "#dcdce3", { padding: "0 10px", textAlign: "right", ...tabular })}
                  />
                  {n(p.amount) > 0 && ron(n(p.amount)) && (
                    <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", fontSize: 11.5, color: t.textMuted, whiteSpace: "nowrap", ...tabular }}>
                      {ron(n(p.amount))}
                    </span>
                  )}
                </div>
                <VatSelect value={p.vat} onChange={(v) => onUpdatePay(p.id, { vat: v })} vatRates={vatRates} />
                <input type="date" value={p.date} onChange={(e) => onUpdatePay(p.id, { date: e.target.value })} style={smallInput(!p.date ? "#e3a09d" : "#dcdce3", { padding: "0 8px" })} />
                <button
                  type="button"
                  className="cw2-danger-hover"
                  title="Remove payment"
                  onClick={() => onRemovePay(p.id)}
                  style={{ width: 30, height: 34, font: "inherit", fontSize: 13, borderRadius: 8, border: `1px solid ${t.border}`, background: "#fff", color: t.textMuted, cursor: "pointer" }}
                >
                  ✕
                </button>
              </div>
            )
          )}

          <div
            onDragOver={(e) => {
              e.preventDefault();
              if (overId !== "balance") setOverId("balance");
            }}
            onDrop={(e) => {
              e.preventDefault();
              if (dragId !== null) onMove(dragId, "balance");
              setDragId(null);
              setOverId(null);
            }}
            style={rowStyle("balance", "#f8f7fe")}
          >
            {handle(visible.length, false)}
            {lockedType("Balance", "Always last · remainder")}
            {lockedAmount(totals.balance, totals.balance < 0)}
            <VatSelect value={balanceVat} onChange={onBalanceVat} vatRates={vatRates} />
            <span style={{ fontSize: 12.5, color: "#6f6f7c" }}>No date — paid at handover</span>
            <LockIcon title="Always the last payment" />
          </div>
        </div>

        <div style={{ padding: "10px 14px", background: t.surfaceSubtle, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <button
            type="button"
            className="cw2-dashed"
            onClick={onAddPay}
            style={{ font: "inherit", fontSize: 12.5, fontWeight: 600, padding: "7px 12px", borderRadius: 8, border: "1px dashed #c9c6f0", background: "#fff", color: t.accent, cursor: "pointer" }}
          >
            + Add payment
          </button>
          <span style={{ fontSize: 12, color: "#6f6f7c", flex: 1, minWidth: 200 }}>
            Payments sort by due date automatically; payments without a date go to the end and can be dragged with ⋮⋮. The balance is always last and takes whatever is left of the contract total.
          </span>
        </div>
      </div>
    </div>
  );
};

export default PaymentsStep;
